'use client';

import React from 'react';

import { ProductDetailLevel3Data } from '@workspace/interfaces';
import { getButtonVariant } from '@workspace/lib';
import { stripInvisibles } from '@workspace/constants';
import { Typography, Button } from '@workspace/ui/common';
import { ProductImageCarousel } from './ProductImageCarousel';
import { ProductVariantSelector } from './ProductVariantSelector';
import { processHeading, type HeadingType } from '@/app/lib/heading-utils';
import { CustomPortableText } from '@workspace/ui/PortableText';
import { useProductDetailTemplate } from './useProductDetailTemplate';

interface ProductDetailLevel3Props {
  product: ProductDetailLevel3Data;
}

export const ProductDetailLevel3 = ({ product }: ProductDetailLevel3Props) => {
  const {
    selectedVariant,
    selectedAttributes,
    carouselKey,
    allImages,
    handleAttributeChange,
    getAvailableValues,
    getAllPossibleValues,
  } = useProductDetailTemplate(product);

  const { name, headlineTitle, productHeadlines, buttons, productDescription, attributeFilters } = product;

  return (
    <div className="porta-container md:py-3xl md:px-4xl p-md">
      <div className="flex flex-col md:flex-row md:gap-3xl gap-4">
        <div className="order-1 md:hidden flex flex-col gap-4">
          <Typography variant="h1" className="font-bold text-secondary-400 leading-[144.44%]" styleVariant="h3">
            {name}
          </Typography>

          {selectedVariant ? (
            <div className="product-variant-container flex flex-col gap-1">
              <Typography variant="bodySm" className="text-sm! text-black font-openSans leading-5">
                {selectedVariant.variantName || 'Product Variant Selection'}
              </Typography>
              {selectedVariant.sku ? (
                <Typography variant="bodySm" className="text-sm! text-black font-openSans leading-5 font-bold!">
                  {selectedVariant.sku}
                </Typography>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="order-2 md:order-1 md:w-1/2 lg:max-w-[489px] space-y-6">
          <ProductImageCarousel key={carouselKey} images={allImages} />

          {headlineTitle ? (
            <div className="hidden md:flex flex-col gap-1">
              {(() => {
                const { text: headingText } = processHeading(headlineTitle as HeadingType);
                return (
                  <Typography
                    variant="h2"
                    className="font-bold text-secondary-400 font-sora leading-[144.44%]"
                    styleVariant="h6"
                  >
                    {headingText}
                  </Typography>
                );
              })()}
              {productHeadlines && productHeadlines.length > 0 ? (
                <div className="prose prose-sm max-w-none">
                  <CustomPortableText value={productHeadlines} />
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="hidden md:flex md:order-2 md:flex-1 flex-col gap-8">
          <div className="flex flex-col gap-4">
            <Typography
              variant="h1"
              className="text-lg! md:!text-4xl font-bold text-secondary-400 leading-[144.44%] md:leading-12"
            >
              {name}
            </Typography>

            {selectedVariant ? (
              <div className="product-variant-container flex flex-col gap-1">
                <Typography
                  variant="bodyMd"
                  className="text-sm! md:!text-base text-black font-openSans leading-5 md:leading-6"
                >
                  {selectedVariant.variantName || 'Product Variant Selection'}
                </Typography>

                {selectedVariant.sku ? (
                  <Typography
                    variant="bodyMd"
                    className="text-sm! md:!text-base text-black font-openSans leading-5 md:leading-[150%] font-bold!"
                  >
                    {selectedVariant.sku}
                  </Typography>
                ) : null}
              </div>
            ) : null}
          </div>

          {buttons && buttons.length > 0 ? (
            <div className="flex flex-row flex-wrap gap-4">
              {buttons.map((button) => (
                <Button
                  key={button._key}
                  variant={getButtonVariant(button.style === 'outline' ? 'tertiary' : button.style)}
                  url={button.link.url}
                  openInNewTab={button.link.type === 'external' ? true : button.link.isOpenInNewTab}
                  className="md:max-w-60 md:min-w-45 truncate min-w-[180px] font-semibold py-3 px-4 leading-5 text-center h-11"
                >
                  {stripInvisibles(button.label || '').trim()}
                </Button>
              ))}
            </div>
          ) : null}

          {productDescription && productDescription.length > 0 ? (
            <div className="prose prose-sm max-w-none">
              <CustomPortableText value={productDescription} />
            </div>
          ) : null}

          {attributeFilters && attributeFilters.length > 0 ? (
            <ProductVariantSelector
              attributeFilters={attributeFilters}
              selectedAttributes={selectedAttributes}
              onAttributeChange={handleAttributeChange}
              getAvailableValues={getAvailableValues}
              getAllPossibleValues={getAllPossibleValues}
            />
          ) : null}
        </div>

        {attributeFilters && attributeFilters.length > 0 ? (
          <div className="order-3 md:hidden">
            <ProductVariantSelector
              attributeFilters={attributeFilters}
              selectedAttributes={selectedAttributes}
              onAttributeChange={handleAttributeChange}
              getAvailableValues={getAvailableValues}
              getAllPossibleValues={getAllPossibleValues}
            />
          </div>
        ) : null}

        {buttons && buttons.length > 0 ? (
          <div className="order-4 md:hidden">
            <div className="flex flex-col gap-3">
              {buttons.map((button) => (
                <Button
                  key={button._key}
                  variant={getButtonVariant(button.style === 'outline' ? 'tertiary' : button.style)}
                  url={button.link.url}
                  openInNewTab={button.link.type === 'external' ? true : button.link.isOpenInNewTab}
                  className="truncate min-w-[180px] font-semibold py-3 px-4 leading-5 text-center h-11"
                >
                  {stripInvisibles(button.label || '').trim()}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        {productDescription && productDescription.length > 0 ? (
          <div className="order-5 md:hidden">
            <div className="prose prose-sm max-w-none">
              <CustomPortableText value={productDescription} />
            </div>
          </div>
        ) : null}

        {headlineTitle ? (
          <div className="order-6 md:hidden flex flex-col gap-1">
            {(() => {
              const { text: headingText } = processHeading(headlineTitle as HeadingType);
              return (
                <Typography variant="h2" className="text-lg! font-bold text-secondary-400 font-sora leading-[144.44%]">
                  {headingText}
                </Typography>
              );
            })()}
            {productHeadlines && productHeadlines.length > 0 ? (
              <div className="prose prose-sm max-w-none">
                <CustomPortableText value={productHeadlines} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
